<template>
  <v-app>
    <v-layout class="shell">
      <v-navigation-drawer
        class="sidebar"
        :class="{ 'sidebar--mobile': isMobileLayout }"
        :model-value="navigationDrawerOpen"
        :permanent="!isMobileLayout"
        :temporary="isMobileLayout"
        width="300"
        @update:model-value="setNavigationDrawerOpen"
      >
        <div class="logo">
          <img
            class="logo__image"
            :src="logoSrc"
            alt="Porsche Croatia"
          />

          <v-btn
            v-if="isMobileLayout"
            class="button button--icon logo__close"
            icon
            variant="text"
            aria-label="Zatvori izbornik"
            @click="setNavigationDrawerOpen(false)"
          >
            <v-icon
              icon="mdi-close"
              size="28"
            />
          </v-btn>
        </div>

        <v-list
          class="menu"
          density="compact"
          nav
        >
          <template
            v-for="section in visibleMenuSections"
            :key="section.key"
          >
            <v-list-item
              class="menu-section"
              rounded="0"
              @click="section.submenus.length && toggleMenuSection(section.key)"
            >
              <v-list-item-title class="menu-text">{{ section.name }}</v-list-item-title>

              <template
                v-if="section.submenus.length || section.showToggle"
                #append
              >
                <v-icon
                  class="menu-section__toggle"
                  :icon="isMenuSectionOpen(section.key) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                />
              </template>
            </v-list-item>

            <Transition name="menu-subitems">
              <div
                v-if="section.submenus.length && isMenuSectionOpen(section.key)"
                class="menu-subitems grid-stack grid-gap-10"
              >
                <v-list-item
                  v-for="submenu in section.submenus"
                  :key="submenu.path"
                  class="menu-subitem"
                  :active="isSubmenuSelected(submenu)"
                  active-class="menu-subitem--selected"
                  :prepend-icon="submenu.icon"
                  :ripple="false"
                  :to="submenu.path"
                >
                  <v-list-item-title class="menu-text">{{ submenu.name }}</v-list-item-title>
                </v-list-item>
              </div>
            </Transition>
          </template>
        </v-list>

        <template
          v-if="isMobileLayout"
          #append
        >
          <div class="sidebar-account">
            <span class="sidebar-account__name">{{ displayName }}</span>

            <v-btn
              class="button button--outline sidebar-account__logout"
              variant="outlined"
              rounded="lg"
              @click="logout"
            >
              Odjava
            </v-btn>
          </div>
        </template>
      </v-navigation-drawer>

      <v-main class="main">
        <AppHeader
          v-if="!isBodyOnlyPage"
          :project-name="headerProjectName"
          :breadcrumbs="headerBreadcrumbs"
          :show-menu-button="isMobileLayout"
          @toggle-menu="openMobileMenu"
        />
        <v-container
          class="page"
          :class="{ 'page--body-only': route.meta.bodyOnly }"
          fluid
        >
          <slot />
        </v-container>

        <AppFooter v-if="!isBodyOnlyPage" />
      </v-main>
    </v-layout>

    <AppToast />
  </v-app>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'

import AppFooter from '../components/AppFooter.vue'
import AppHeader from '../components/AppHeader.vue'
import AppToast from '../components/AppToast.vue'
import logoSrc from '../assets/images/porsche-croatia-logo.svg'
import { hasAllPermissions } from '../auth/authorization'
import { useMsal } from '../auth/useMsal'
import { menuSections } from '../config/menu.config'
import { getActivityEventAsync } from '../services/offlineService'

const route = useRoute()
const { instance, accounts } = useMsal()
const { mobile: isBodyOnlyMobile } = useDisplay({ mobileBreakpoint: 768 })
const { mobile: isMobileLayout } = useDisplay({ mobileBreakpoint: 1201 })
const openMenuSections = ref(['events'])
const mobileMenuOpen = ref(false)
const eventBreadcrumbName = ref(null)
const inviteeBreadcrumbName = ref(null)

const visibleMenuSections = computed(() => menuSections
  .filter(canAccessMenuItem)
  .map((section) => ({
    ...section,
    submenus: section.submenus.filter(canAccessMenuItem),
  })))

const activeSubmenu = computed(() => {
  let selectedSubmenu = null

  visibleMenuSections.value.forEach((section) => {
    section.submenus.forEach((submenu) => {
      if (isRouteInSubmenu(submenu) && (!selectedSubmenu || submenu.path.length > selectedSubmenu.path.length)) {
        selectedSubmenu = submenu
      }
    })
  })

  return selectedSubmenu
})

const activeMenuSection = computed(() => (
  visibleMenuSections.value.find((section) => section.submenus.some((submenu) => submenu.path === activeSubmenu.value?.path))
))

const headerProjectName = computed(() => {
  if (!activeMenuSection.value) {
    return 'Vento'
  }

  return `Vento | ${activeMenuSection.value.headerName ?? activeMenuSection.value.name}`
})

const headerBreadcrumbs = computed(() => {
  const breadcrumb = route.meta.breadcrumb ?? {}
  const eventName = breadcrumb.showEventName
    ? eventBreadcrumbName.value
    : null
  const inviteeName = breadcrumb.showInviteeName
    ? inviteeBreadcrumbName.value
    : null

  return [activeSubmenu.value?.name, eventName, inviteeName, breadcrumb.label].filter(Boolean)
})
const isBodyOnlyPage = computed(() => route.meta.bodyOnly && isBodyOnlyMobile.value)
const navigationDrawerOpen = computed(() => !isMobileLayout.value || mobileMenuOpen.value)
const displayName = computed(() => accounts.value[0]?.name || accounts.value[0]?.username || '')

onMounted(() => {
  if (activeMenuSection.value && !openMenuSections.value.includes(activeMenuSection.value.key)) {
    openMenuSections.value.push(activeMenuSection.value.key)
  }
})

watch(() => route.fullPath, () => {
  if (isMobileLayout.value) {
    mobileMenuOpen.value = false
  }
})

watch(
  () => [
    route.params.eventId,
    route.params.inviteeId,
    route.meta.breadcrumb?.showEventName,
    route.meta.breadcrumb?.showInviteeName,
  ],
  ([eventId, inviteeId, showEventName, showInviteeName]) => (
    loadEventBreadcrumbAsync(eventId, inviteeId, showEventName, showInviteeName)
  ),
  { immediate: true }
)

async function loadEventBreadcrumbAsync (eventId, inviteeId, showEventName, showInviteeName) {
  eventBreadcrumbName.value = null
  inviteeBreadcrumbName.value = null

  if (!eventId || (!showEventName && !showInviteeName)) {
    return
  }

  try {
    const event = await getActivityEventAsync(eventId)

    if (route.params.eventId !== eventId) {
      return
    }

    if (route.meta.breadcrumb?.showEventName) {
      eventBreadcrumbName.value = event.name
    }
    if (route.meta.breadcrumb?.showInviteeName && route.params.inviteeId === inviteeId) {
      inviteeBreadcrumbName.value = event.invitees.find(({ id }) => id === inviteeId)?.fullName ?? null
    }
  } catch (error) {
    console.error('Failed to load event breadcrumb.', error)
  }
}

function openMobileMenu () {
  mobileMenuOpen.value = true
}

function setNavigationDrawerOpen (isOpen) {
  mobileMenuOpen.value = isOpen
}

function logout () {
  instance.logoutRedirect()
}

function toggleMenuSection (sectionKey) {
  if (openMenuSections.value.includes(sectionKey)) {
    openMenuSections.value = openMenuSections.value.filter((key) => key !== sectionKey)
    return
  }

  openMenuSections.value.push(sectionKey)
}

function isMenuSectionOpen (sectionKey) {
  return openMenuSections.value.includes(sectionKey)
}

function isRouteInSubmenu (submenu) {
  return route.path === submenu.path || route.path.startsWith(`${submenu.path}/`)
}

function isSubmenuSelected (submenu) {
  return activeSubmenu.value?.path === submenu.path
}

function canAccessMenuItem (menuItem) {
  const requiredPermissions = menuItem.requiredPermissions ?? []

  return hasAllPermissions(requiredPermissions)
}
</script>

<style
  scoped
  lang="scss"
>
.shell {
  min-height: 100vh;
  background: rgb(var(--v-theme-background));
}

.sidebar {
  --sidebar-logo-height: 144px;
  --sidebar-logo-padding: 43px 0 0;
  --sidebar-logo-width: 217px;
  --sidebar-logo-justify-content: center;

  background: rgb(var(--v-theme-navigation));
  border-right: 1px solid rgb(var(--v-theme-border));

  &--mobile {
    --sidebar-logo-height: 100px;
    --sidebar-logo-padding: 28px 0 0 48px;
    --sidebar-logo-width: 160px;
    --sidebar-logo-justify-content: flex-start;
  }
}

.logo {
  position: relative;
  height: var(--sidebar-logo-height);
  display: flex;
  align-items: flex-start;
  justify-content: var(--sidebar-logo-justify-content);
  padding: var(--sidebar-logo-padding);

  &__image {
    display: block;
    width: var(--sidebar-logo-width);
    max-width: 100%;
    max-height: 50px;
    object-fit: contain;
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 14px;
    width: 30px;
    height: 44px;
    color: var(--color-black);
  }
}

.sidebar-account {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.4);

  &__name {
    min-width: 0;
    overflow: hidden;
    color: var(--color-black);
    font-size: clamp-bp("sm", "xl", 15, 16);
    font-weight: 600;
    line-height: clamp-bp("sm", "xl", 22, 24);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__logout {
    flex: 0 0 auto;
    width: 82px;
    height: 44px;
  }
}

.menu {
  width: 220px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
}

.menu-section {
  width: 220px;
  min-height: 50px;
  padding: 13px 0;
  color: rgb(var(--v-theme-primary));
  font-weight: 700;

  :deep(.v-list-item-title) {
    font-weight: 700;
  }

  :deep(.menu-section__toggle) {
    width: 24px;
    height: 24px;
    color: var(--color-menu-text);
    flex-shrink: 0;
    font-size: clamp-bp("sm", "xl", 22, 24);
    opacity: 1;
  }

  :deep(.v-list-item__append) {
    margin-inline-start: 4px;
  }

  :deep(.v-list-item__spacer) {
    width: 4px !important;
  }
}

.menu-subitems {
  padding: 0 0 24px;
}

.menu-subitems-enter-active,
.menu-subitems-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.menu-subitems-enter-from,
.menu-subitems-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.menu-subitem {
  --v-activated-opacity: 0;
  --v-focus-opacity: 0;
  --v-hover-opacity: 0;
  --v-pressed-opacity: 0;
  width: 220px;
  min-height: 50px;
  padding: 13px 7px 13px 14px;
  border-radius: 0;
  color: var(--color-text-body);

  :deep(.v-list-item__overlay) {
    opacity: 0;
  }

  :deep(.v-list-item__prepend) {
    align-items: center;
    align-self: center;
    margin-inline-end: 0;
  }

  :deep(.v-list-item__prepend > .v-icon) {
    width: 24px;
    height: 24px;
    color: var(--color-menu-text);
    font-size: clamp-bp("sm", "xl", 22, 24);
    opacity: 1;
  }

  :deep(.v-list-item__spacer) {
    width: 13px !important;
  }

  :deep(.v-list-item-title) {
    font-weight: 300;
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
  }

  &.menu-subitem--selected {
    background: rgba(var(--v-theme-navigationActive), 0.15);
    border-left: 3px solid var(--color-menu-text);
    border-radius: 0 10px 10px 0;
    color: var(--color-menu-text);
    font-weight: 700;
  }

  &--selected {
    :deep(.v-list-item__overlay) {
      opacity: 0;
    }

    :deep(.v-icon) {
      opacity: 1;
    }

    :deep(.v-list-item-title) {
      font-weight: 500;
    }
  }
}

.main {
  background: rgb(var(--v-theme-background));
}

.page {
  --page-padding-block: 16px;
  --page-padding-inline: 16px;

  width: 100%;
  min-height: 100vh;
  padding: var(--page-padding-block) var(--page-padding-inline);

  @include breakpoint-up("sm") {
    --page-padding-block: 30px;
    --page-padding-inline: 30px;
  }

  &--body-only {
    padding: 0;

    @include breakpoint-up("sm") {
      padding: var(--page-padding-block) var(--page-padding-inline);
    }
  }
}
</style>
